import { useState } from 'react';

import type {
  HitlActionRequest,
  HitlActionType,
  HitlItem,
} from '../services/types';

import { Button } from './Button';

export interface UnitConversionCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

const KEEP_AS_IS_SENTINEL = 'keep_as_is';

/**
 * v0.5 Stage F.4 — UI card for the ``unit_conversion`` HITL.
 *
 * Fired by ``measurement_cleaner`` when a column has multiple units
 * in the same quantity-dimension (e.g. ``20 kg, 45 lb, 10 kg``).
 * Reviewer picks one of the detected units (Raydn converts every
 * row into that unit on the next run) or ``keep_as_is`` (suppresses
 * the HITL re-fire on subsequent runs without applying any
 * conversion).
 *
 * The dispatch shape:
 *   ``{type: "choose_unit_target", payload: {target_unit: "<unit>"}}``
 *
 * Backend writes a ``column_policy`` audit row keyed on
 * ``(file_hash, column)`` so carry-forward applies to same-bytes
 * re-uploads automatically.
 *
 * Lives outside HitlQueue.tsx so the existing column-datatype /
 * format-decision / table-boundary / cross-field-rule card paths
 * stay untouched. The routing branch in HitlQueue delegates to
 * this component when ``hit.resolution_type === "unit_conversion"``.
 */
export function UnitConversionCard({
  hit,
  onAction,
  reviewerId,
}: UnitConversionCardProps) {
  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const detectedOptions = (hit.options ?? []).filter(
    (o) => o !== KEEP_AS_IS_SENTINEL,
  );

  const onConfirm = async () => {
    if (!selected) return;
    setPending('choose_unit_target');
    setError(null);
    try {
      await onAction(hit.hit_id, {
        type: 'choose_unit_target',
        payload: { target_unit: selected },
        reviewer_id: reviewerId,
      });
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  return (
    <li
      data-rule-card="unit-conversion"
      data-resolution-type="unit_conversion"
      className={`rounded-md border bg-paper border-l-4 border-l-warn border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="m-0 text-sm font-semibold flex-1">
            <code>{hit.column}</code> · pick a target unit
          </h3>
          <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
            unit conversion
          </span>
        </div>
        <p className="text-xs text-muted m-0">{hit.issue}</p>
      </div>

      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              Convert every row in this column to:
            </legend>
            {detectedOptions.map((unit) => (
              <label
                key={unit}
                className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30"
              >
                <input
                  type="radio"
                  name={`unit-${hit.hit_id}`}
                  value={unit}
                  checked={selected === unit}
                  onChange={() => setSelected(unit)}
                  className="mt-0.5"
                  data-testid={`unit-radio-${unit}`}
                />
                <span className="flex-1">
                  <span className="block font-mono text-sm">{unit}</span>
                  <span className="block text-xs text-muted mt-0.5">
                    Convert every row to <code>{unit}</code>; rows in a
                    different quantity-dimension are anomaly-flagged and
                    pass through unchanged.
                  </span>
                </span>
              </label>
            ))}
            <label className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30">
              <input
                type="radio"
                name={`unit-${hit.hit_id}`}
                value={KEEP_AS_IS_SENTINEL}
                checked={selected === KEEP_AS_IS_SENTINEL}
                onChange={() => setSelected(KEEP_AS_IS_SENTINEL)}
                className="mt-0.5"
                data-testid="unit-radio-keep_as_is"
              />
              <span className="flex-1">
                <span className="block font-medium text-sm">
                  Keep as-is
                </span>
                <span className="block text-xs text-muted mt-0.5">
                  Leave the column with mixed units. Raydn won't ask
                  again on subsequent uploads of the same file.
                </span>
              </span>
            </label>
          </fieldset>
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={selected === null || pending !== null}
              data-testid="unit-confirm"
            >
              {pending === 'choose_unit_target' ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
